import { usePrivateToast } from "@/shared/usePrivateToast";
import { canUseBackOffice } from "@/modules/auth/backOffice";
import { usePrivateApi } from "@/shared/usePrivateApi";
import { countedKey } from "@/lib/i18n/plural";
import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Checkbox, Chip, ConfirmModal, EmptyState, ErrorState, LoadingSkeleton } from "@a2z/ui";

import { endpoints } from "@/lib/api/endpoints";
import { useI18n, useTranslate } from "@/lib/i18n";
import { queryKeys, useApiQuery } from "@/shared/queries";
import { useErrorMessage } from "@/shared/errors";
import { Screen } from "@/shared/ui";
import {
  PERMISSIONS,
  hasPermission,
  useSession,
} from "@/modules/auth/useSession";
import { useLogout } from "@/modules/auth/useLogout";
import type {
  KitchenAvailabilityIngredient,
  KitchenAvailabilityItem,
  KitchenAvailabilityResponse,
  KitchenStation,
} from "@/lib/api/types";

/**
 * 86'ing, from the kitchen (D32, D50).
 *
 * ─── WHERE THIS SCREEN IS READ ───────────────────────────────────────────────
 * The same wall tablet the board is on, by the same person: hands full, two or
 * three steps away, mid-service. So it borrows the board's sizing — a bare
 * `Screen`, nothing under 14px, 44px+ controls, state stated in a WORD as well
 * as a tone — rather than the back office's density.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Reads `GET /restaurant/kitchen-availability`, which carries names,
 * availability and stations and **no price**: the kitchen holds
 * `RESTAURANT_MENU_AVAILABILITY` and deliberately not `RESTAURANT_MENU_VIEW`,
 * the same reason a chit carries no prices. Writes through the existing
 * `PUT /restaurant/menu-items/:productId/availability`, which accepts either
 * that grant or `RESTAURANT_MENU_MANAGE`.
 *
 * Every flip is CONFIRMED first. An accidental brush against a wall screen is
 * the ordinary failure mode here, and 86'ing pulls a dish off the till and off
 * the QR menu for every diner in the room until someone puts it back.
 *
 * ─── TWO PANES SINCE B18 (unit 6) ────────────────────────────────────────────
 * The screen now carries INGREDIENTS beside dishes, and they are two panes
 * rather than one list because they are two different claims and the founder's
 * answer turns on the difference: an 86'd DISH cannot be sold at all, while a
 * dish whose shrimp is finished still sells and the waiter is merely warned.
 * Interleaving them under the station headings would have said the two acts
 * were the same act.
 *
 * The switch is a pair of full-height BUTTONS rather than the shared
 * `Segmented`, which is 34px: this screen's whole sizing argument is 44px+
 * controls read at arm's length with hands full, and a 34px pill would be the
 * one control on it a cook has to aim at. The ingredient toggle writes to
 * `PUT /restaurant/ingredients/:id/availability`, which takes the same
 * `RESTAURANT_MENU_AVAILABILITY` this screen is already gated on.
 */
export function KitchenAvailabilityPage() {
  const { apiFetch } = usePrivateApi();
  const toast = usePrivateToast();
  const t = useTranslate();
  const { locale } = useI18n();
  const queryClient = useQueryClient();
  const errorMessage = useErrorMessage();
  const { data: session } = useSession();
  // Sign-out and the language switch ride the `Screen` header ungated, exactly
  // as they do on the board: they belong to whoever is standing in front of a
  // shared tablet, and the KITCHEN preset holds no back-office grant.
  const { logout, isLoggingOut } = useLogout();

  const [pending, setPending] = useState<KitchenAvailabilityItem | null>(null);
  const [pendingIngredient, setPendingIngredient] =
    useState<KitchenAvailabilityIngredient | null>(null);
  const [pane, setPane] = useState<"dishes" | "ingredients">("dishes");
  const [isSaving, setSaving] = useState(false);
  /**
   * T11 / D169 — «حتى إتاحته يدويًا»: this 86 holds past the business-day
   * reset. Asked on the way OUT only (putting back clears it server-side), and
   * reset each time a confirm opens.
   */
  const [untilManual, setUntilManual] = useState(false);
  const askDish = (item: KitchenAvailabilityItem) => {
    setUntilManual(false);
    setPending(item);
  };
  const askIngredient = (row: KitchenAvailabilityIngredient) => {
    setUntilManual(false);
    setPendingIngredient(row);
  };

  // Gated BEFORE the fetch, the house rule: the endpoint is
  // MENU_AVAILABILITY-gated, so anyone else would otherwise read a 403 as a
  // failure state.
  const canToggle = hasPermission(session, PERMISSIONS.MENU_AVAILABILITY);
  const canSeeBoard = hasPermission(session, PERMISSIONS.KITCHEN_VIEW);
  const canLeaveToManage = canUseBackOffice(session);

  const list = useApiQuery<KitchenAvailabilityResponse>(
    queryKeys.kitchenAvailability,
    endpoints.restaurant.kitchenAvailability,
    { enabled: canToggle },
  );

  /*
   * The way back, decided by capability like the landing rule
   * (`modules/auth/landing.ts`). A cook came from the board and goes back to
   * it; a cashier who typed the URL holds no KITCHEN_VIEW and would land on
   * the board's denial, so they are sent to the back office instead. Someone
   * with neither gets no control rather than a link onto a 403.
   */
  const backTarget = canSeeBoard
    ? `/${locale}/kitchen`
    : canLeaveToManage
      ? `/${locale}/manage`
      : null;
  const back = backTarget ? (
    <Link
      to={backTarget}
      aria-label={t((m) => m.kitchen.availabilityBack)}
      title={t((m) => m.kitchen.availabilityBack)}
      className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[10px] border border-[color:var(--border)] text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--surface-alt)] hover:text-[color:var(--text)]"
      data-testid="kitchen-availability-back"
    >
      {/* A back ARROW, not the board's X: this screen sits under the board, so
          there is somewhere to step back TO. It flips with the writing
          direction, and it does so through the LOGICAL variant (UX-round D51):
          «UX-round Dn» = the 2026-09-17 UX round's own numbering (`.roundtable/2026-09-17-restaurant-ux/decisions.md`), not §11's.
          `rtl:-scale-x-100` follows `dir` on the ancestor, where
          `locale === "ar" ? …` was a direction branch in JSX — the one thing
          `report-pages.md`'s ship checklist forbids by name, and a second
          source of truth about direction beside the one the document already
          carries. Five siblings in this app already use the logical form. */}
      <svg
        width="17"
        height="17"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        className="rtl:-scale-x-100"
      >
        <path d="M19 12H5M12 19l-7-7 7-7" />
      </svg>
    </Link>
  ) : null;

  const items = useMemo(() => list.data?.items ?? [], [list.data]);
  const stations = useMemo(() => list.data?.stations ?? [], [list.data]);

  const stationLabel = useCallback(
    (station: KitchenStation) =>
      locale === "en" && station.nameEn ? station.nameEn : station.nameAr,
    [locale],
  );

  const itemLabel = useCallback(
    (item: KitchenAvailabilityItem) =>
      locale === "en" && item.nameEn ? item.nameEn : item.nameAr,
    [locale],
  );

  /**
   * Grouped by station, in the stations' own order, with everything unassigned
   * (or pointed at a station this store has since deleted) under one trailing
   * heading. Grouping rather than a filter: a cook reads their own section and
   * a flat list of the whole menu on a wall screen is a search task.
   */
  const groups = useMemo(() => {
    const known = new Set(stations.map((station) => station.id));
    const rows: {
      key: string;
      title: string;
      items: KitchenAvailabilityItem[];
    }[] = stations.map((station) => ({
      key: station.id,
      title: stationLabel(station),
      items: items.filter((item) => item.stationId === station.id),
    }));
    const rest = items.filter(
      (item) => item.stationId === null || !known.has(item.stationId),
    );
    if (rest.length > 0) {
      rows.push({
        key: "unassigned",
        title: t((m) => m.kitchen.availabilityUnassigned),
        items: rest,
      });
    }
    return rows.filter((group) => group.items.length > 0);
  }, [items, stationLabel, stations, t]);

  const soldOutCount = items.filter((item) => !item.isAvailable).length;
  const ingredients = useMemo(
    () => list.data?.ingredients ?? [],
    [list.data],
  );
  const finishedCount = ingredients.filter((row) => !row.isAvailable).length;

  /**
   * Call an ingredient finished, or put it back (B18).
   *
   * Invalidates the MENU cache as well as this one, exactly as the dish
   * toggle does and for the same reason one step removed: the till reads its
   * warning off `GET /restaurant/menu-items`, so that query is stale the
   * moment this lands. Nothing here busts the buyer's storefront cache —
   * an ingredient 86 changes nothing a diner can see, which is the whole
   * difference between it and the dish toggle above.
   */
  const confirmIngredient = async () => {
    if (!pendingIngredient) return;
    setSaving(true);
    try {
      await apiFetch(
        endpoints.restaurant.ingredientAvailability(
          pendingIngredient.ingredientId,
        ),
        {
          method: "PUT",
          body: pendingIngredient.isAvailable
            ? { isAvailable: false, untilManual }
            : { isAvailable: true },
        },
      );
      await queryClient.invalidateQueries({
        queryKey: queryKeys.kitchenAvailability,
      });
      await queryClient.invalidateQueries({ queryKey: queryKeys.menuItems });
      await queryClient.invalidateQueries({ queryKey: queryKeys.ingredients, exact: true });
      await queryClient.invalidateQueries({ queryKey: queryKeys.ingredientAvailabilityHistory(pendingIngredient.ingredientId) });
      toast.success(
        pendingIngredient.isAvailable
          ? t((m) => m.kitchen.availabilityIngredientMarkedOut, {
              item: pendingIngredient.name,
            })
          : t((m) => m.kitchen.availabilityIngredientMarkedBack, {
              item: pendingIngredient.name,
            }),
      );
      setPendingIngredient(null);
    } catch (failure) {
      toast.error(errorMessage(failure));
    } finally {
      setSaving(false);
    }
  };

  const confirm = async () => {
    if (!pending) return;
    setSaving(true);
    try {
      await apiFetch(
        endpoints.restaurant.menuItemAvailability(pending.productId),
        {
          method: "PUT",
          body: pending.isAvailable ? { isAvailable: false, untilManual } : { isAvailable: true },
        },
      );
      await queryClient.invalidateQueries({
        queryKey: queryKeys.kitchenAvailability,
      });
      // The till and the back-office menu read the same flag from their own
      // endpoints, so their caches are stale the moment this lands.
      await queryClient.invalidateQueries({ queryKey: queryKeys.menuItems });
      toast.success(
        pending.isAvailable
          ? t((m) => m.kitchen.availabilityMarkedOut, {
              item: itemLabel(pending),
            })
          : t((m) => m.kitchen.availabilityMarkedBack, {
              item: itemLabel(pending),
            }),
      );
      setPending(null);
    } catch (failure) {
      toast.error(errorMessage(failure));
    } finally {
      setSaving(false);
    }
  };

  // Server-side `@RequirePermission` is the real gate; this is so a waiter who
  // lands on the URL gets an explanation instead of a screen full of 403s.
  if (session && !canToggle) {
    return (
      <Screen
        title={t((m) => m.kitchen.availabilityTitle)}
        leading={back}
        onLogout={logout}
        isLoggingOut={isLoggingOut}
      >
        <EmptyState
          title={t((m) => m.kitchen.availabilityDeniedTitle)}
          description={t((m) => m.kitchen.availabilityDeniedBody)}
        />
      </Screen>
    );
  }

  return (
    <Screen
      /* One title for both panes (T14): «النواقص» names what is short —
         dishes or ingredients — and the tabs say which list is open. */
      title={t((m) => m.kitchen.availabilityTitle)}
      leading={back}
      onLogout={logout}
      isLoggingOut={isLoggingOut}
      action={
        // Counts the pane in front of the reader, not both. Two panes carrying
        // two different claims must not share one number in the header — a
        // combined count would answer neither question.
        <span className="text-sm font-bold text-[color:var(--text-secondary)]">
          {pane === "dishes"
            ? t(
                // B117/A2 — «1 نفدوا» is a plural verb about one dish.
                countedKey(soldOutCount, {
                  one: (m) => m.kitchen.availabilitySoldOutCountOne,
                  two: (m) => m.kitchen.availabilitySoldOutCountTwo,
                  /* QA3 — this noun was excused a `few` arm as "spelling no
                     noun that moves". The noun is elided and the VERB moves:
                     «٣ نفدوا» takes the plural, «١٢ نفد» the singular, and the
                     catalogue said so all along — `…One` is «واحد نفد». */
                  few: (m) => m.kitchen.availabilitySoldOutCountFew,
                  many: (m) => m.kitchen.availabilitySoldOutCount,
                }),
                { count: soldOutCount },
              )
            : t(
    countedKey(finishedCount, {
      one: (m) => m.kitchen.availabilityIngredientsCountOne,
      two: (m) => m.kitchen.availabilityIngredientsCountTwo,
      few: (m) => m.kitchen.availabilityIngredientsCountFew,
      many: (m) => m.kitchen.availabilityIngredientsCount,
    }),
    {
                count: finishedCount,
              })}
        </span>
      }
    >
      {/* The pane switch (B18). Two buttons at the screen's own touch size
          rather than the 34px shared `Segmented`: everything else here is 44px+
          because it is read at arm's length with hands full, and the one
          control a cook has to aim at should not be the navigation.
          `aria-pressed` rather than tab semantics — these are two toggles over
          one region, and a tablist would promise arrow-key navigation this
          screen does not implement. */}
      <div className="mb-4 flex gap-2" data-testid="kitchen-availability-panes">
        <Button
          type="button"
          size="touch"
          variant={pane === "dishes" ? "primary" : "outline"}
          aria-pressed={pane === "dishes"}
          onClick={() => setPane("dishes")}
          className="flex-1"
          data-testid="kitchen-availability-pane-dishes"
        >
          {t((m) => m.kitchen.availabilityTabDishes)}
        </Button>
        <Button
          type="button"
          size="touch"
          variant={pane === "ingredients" ? "primary" : "outline"}
          aria-pressed={pane === "ingredients"}
          onClick={() => setPane("ingredients")}
          className="flex-1"
          data-testid="kitchen-availability-pane-ingredients"
        >
          {t((m) => m.kitchen.availabilityTabIngredients)}
        </Button>
      </div>

      {/* THE INTRO PARAGRAPH IS GONE (UX-round D46).

          It was four sentences rendered unconditionally above the rows, on the
          screen a cook opens BECAUSE something just ran out — the least
          patient moment of the night. At 1024×768 it wrapped to three lines
          and, with the tabs and the heading, ate roughly a third of the screen
          before the first dish row. `inventory.md:198` lists it among the
          twelve longest strings in the app and scores this page at 65% prose.

          Nothing left the app (charter invariant 6, and the spec keeper
          checked each destination rather than taking it on trust): the till/QR
          consequence is in `availabilityConfirmOutBody`, at the moment of the
          act; «على التلت بيفضل باين ومكتوب عليه نفد» is the `pos.menuSoldOut`
          chip with `pos.menuSoldOutHint` under it on the waiter's own tile;
          «مش بيتشال من المنيو» is `pos.menuMarkSoldOutBody` verbatim; and "put
          it back with one tap" is the `availabilityPutBack` button sitting in
          the row. The ingredients pane's two claims are carried, word for
          word, by `availabilityIngredientConfirmOutBody`. */}

      {list.isLoading ? (
        <div
          aria-busy="true"
          aria-live="polite"
          aria-label={t((m) => m.common.loading)}
        >
          <LoadingSkeleton rows={6} />
        </div>
      ) : null}

      {list.error ? (
        <ErrorState
          message={errorMessage(list.error)}
          retryLabel={t((m) => m.common.retry)}
          onRetry={() =>
            void queryClient.invalidateQueries({
              queryKey: queryKeys.kitchenAvailability,
            })
          }
        />
      ) : null}

      {list.data && pane === "dishes" && items.length === 0 ? (
        <EmptyState
          title={t((m) => m.kitchen.availabilityEmptyTitle)}
          description={t((m) => m.kitchen.availabilityEmptyBody)}
        />
      ) : null}

      {/* Its own empty state, deliberately: a store with a menu but no recipes
          has dishes and no ingredients, and the dish pane's copy ("ask the
          owner to add dishes") would send a cook after the wrong thing. */}
      {list.data && pane === "ingredients" && ingredients.length === 0 ? (
        <EmptyState
          title={t((m) => m.kitchen.availabilityIngredientEmptyTitle)}
          description={t((m) => m.kitchen.availabilityIngredientEmptyBody)}
        />
      ) : null}

      {pane === "ingredients" ? (
        <section className="mb-6 flex flex-col gap-2">
          {ingredients.map((row) => (
            <div
              key={row.ingredientId}
              data-testid={`kitchen-ingredient-${row.ingredientId}`}
              className={[
                "flex min-h-14 flex-wrap items-center gap-3 rounded-xl border bg-[color:var(--surface)] px-4 py-3",
                row.isAvailable
                  ? "border-[color:var(--border)]"
                  : // Tone AND the word beside it — never colour alone.
                    "border-[color:var(--warning)]",
              ].join(" ")}
            >
              <span className="min-w-0 flex-1 text-base font-extrabold">
                {row.name}
              </span>
              <Chip
                tone={row.isAvailable ? "success" : "warning"}
                size="md"
                data-testid={`kitchen-ingredient-state-${row.ingredientId}`}
              >
                {row.isAvailable
                  ? t((m) => m.kitchen.availabilityIngredientOn)
                  : t((m) => m.kitchen.availabilityIngredientOff)}
              </Chip>
              {!row.isAvailable && row.untilManual ? (
                <Chip
                  tone="neutral"
                  size="md"
                  data-testid={`kitchen-ingredient-until-manual-${row.ingredientId}`}
                >
                  {t((m) => m.kitchen.availabilityIngredientUntilManual)}
                </Chip>
              ) : null}
              {/* `outline` in BOTH directions (UX-round D50).

                  It was `danger-outline` for calling an ingredient finished —
                  red on every available row, so the screen was red top to
                  bottom for an act a cook performs six times a shift and undoes
                  in one tap. Meanwhile the board spends `--error-500` on the
                  thing that genuinely is an emergency: a remade plate. Red that
                  is spent on "the chicken ran out" has stopped meaning anything
                  by Thursday. The confirm behind it already carries `caution`,
                  which is the rung this act belongs on. */}
              <Button
                type="button"
                size="touch"
                variant="outline"
                onClick={() => askIngredient(row)}
                data-testid={`kitchen-ingredient-toggle-${row.ingredientId}`}
              >
                {row.isAvailable
                  ? t((m) => m.kitchen.availabilityIngredientMarkOut)
                  : t((m) => m.kitchen.availabilityIngredientPutBack)}
              </Button>
            </div>
          ))}
        </section>
      ) : null}

      {(pane === "dishes" ? groups : []).map((group) => (
        <section key={group.key} className="mb-6 flex flex-col gap-2">
          <h2 className="text-sm font-extrabold uppercase tracking-[0.06em] text-[color:var(--text-secondary)]">
            {group.title}
          </h2>
          {group.items.map((item) => (
            <div
              key={item.productId}
              data-testid={`kitchen-availability-${item.productId}`}
              className={[
                "flex min-h-14 items-center gap-3 rounded-xl border bg-[color:var(--surface)] px-4 py-3",
                item.isAvailable
                  ? "border-[color:var(--border)]"
                  : // Tone AND the word below — never colour alone.
                    "border-[color:var(--warning)]",
              ].join(" ")}
            >
              <span className="min-w-0 flex-1 text-base font-extrabold">
                {itemLabel(item)}
              </span>
              <Chip
                tone={item.isAvailable ? "success" : "warning"}
                size="md"
                data-testid={`kitchen-availability-state-${item.productId}`}
              >
                {item.isAvailable
                  ? t((m) => m.kitchen.availabilityOn)
                  : item.unavailableBecause === "COMPONENT"
                    ? // A combo blocked from inside reads differently from a
                      // dish someone turned off: same "off", different cause,
                      // and only one of them is this screen's to fix.
                      t((m) => m.kitchen.availabilityOffComponent)
                    : t((m) => m.kitchen.availabilityOff)}
              </Chip>
              {!item.isAvailable && item.untilManual ? (
                <Chip
                  tone="neutral"
                  size="md"
                  data-testid={`kitchen-availability-until-manual-${item.productId}`}
                >
                  {t((m) => m.kitchen.availabilityUntilManual)}
                </Chip>
              ) : null}
              {/* The toggle. A button rather than a switch because it opens a
                  confirm: a switch that does not move until a dialog is
                  answered lies about its own state for as long as the dialog
                  is open.

                  `outline` in both directions (UX-round D50) — see the ingredient row
                  above for why the red went. */}
              {/* A combo blocked BY A COMPONENT has no toggle, because there
                  is nothing here to toggle: its own flag is already on, and
                  flipping it would change no number the cook can see while
                  reading as though the plate had been fixed. The row says
                  what has to happen instead — put the component back — which
                  is a row further down this same screen. */}
              {item.unavailableBecause === "COMPONENT" ? (
                <span
                  className="text-[12px] font-bold text-[color:var(--text-muted)]"
                  data-testid={`kitchen-availability-blocked-${item.productId}`}
                >
                  {t((m) => m.kitchen.availabilityBlockedHint)}
                </span>
              ) : (
                <Button
                  type="button"
                  size="touch"
                  variant="outline"
                  onClick={() => askDish(item)}
                  data-testid={`kitchen-availability-toggle-${item.productId}`}
                >
                  {item.isAvailable
                    ? t((m) => m.kitchen.availabilityMarkOut)
                    : t((m) => m.kitchen.availabilityPutBack)}
                </Button>
              )}
            </div>
          ))}
        </section>
      ))}

      <ConfirmModal
        isOpen={pending !== null}
        onClose={() => setPending(null)}
        onConfirm={() => void confirm()}
        /* One tone for both directions, and it is `caution` — the same the POS
           tile's confirm wears (`useMenuAvailability.tsx`). Neither direction
           is `danger`: nothing here moves money or marks a signed document,
           and both are undone in one tap by the person standing here. Neither
           is `primary` either: both reach every till and the guests' QR menu
           before that tap could happen. The body names the reversal. */
        tone="caution"
        title={
          pending?.isAvailable
            ? t((m) => m.kitchen.availabilityConfirmOutTitle)
            : t((m) => m.kitchen.availabilityConfirmBackTitle)
        }
        description={
          pending?.isAvailable
            ? t((m) => m.kitchen.availabilityConfirmOutBody, {
                item: pending ? itemLabel(pending) : "",
              })
            : t((m) => m.kitchen.availabilityConfirmBackBody, {
                item: pending ? itemLabel(pending) : "",
              })
        }
        confirmLabel={
          pending?.isAvailable
            ? t((m) => m.kitchen.availabilityMarkOut)
            : t((m) => m.kitchen.availabilityPutBack)
        }
        cancelLabel={t((m) => m.common.cancel)}
        loadingLabel={t((m) => m.kitchen.availabilitySaving)}
        isSubmitting={isSaving}
        closeLabel={t((m) => m.common.cancel)}
      >
        {pending?.isAvailable ? (
          <Checkbox
            size="lg"
            checked={untilManual}
            onChange={(event) => setUntilManual(event.target.checked)}
            label={t((m) => m.kitchen.availabilityUntilManual)}
            description={t((m) => m.kitchen.availabilityUntilManualHint)}
            data-testid="kitchen-availability-until-manual-check"
          />
        ) : null}
      </ConfirmModal>

      {/* A confirm of its own, and the copy is what makes the two acts
          distinguishable at the moment of the tap (B18): it says the dishes
          STAY orderable. A cook reaching for this expecting the dish pane's
          behaviour would otherwise find out at the till. */}
      <ConfirmModal
        isOpen={pendingIngredient !== null}
        onClose={() => setPendingIngredient(null)}
        onConfirm={() => void confirmIngredient()}
        /* `caution` in both directions, for the reason above — and this act
           reaches further than the dish one, not less: every waiter adding a
           dish that contains the ingredient meets a warning. */
        tone="caution"
        title={
          pendingIngredient?.isAvailable
            ? t((m) => m.kitchen.availabilityIngredientConfirmOutTitle)
            : t((m) => m.kitchen.availabilityIngredientConfirmBackTitle)
        }
        description={
          pendingIngredient?.isAvailable
            ? t((m) => m.kitchen.availabilityIngredientConfirmOutBody, {
                item: pendingIngredient ? pendingIngredient.name : "",
              })
            : t((m) => m.kitchen.availabilityIngredientConfirmBackBody, {
                item: pendingIngredient ? pendingIngredient.name : "",
              })
        }
        confirmLabel={
          pendingIngredient?.isAvailable
            ? t((m) => m.kitchen.availabilityIngredientMarkOut)
            : t((m) => m.kitchen.availabilityIngredientPutBack)
        }
        cancelLabel={t((m) => m.common.cancel)}
        loadingLabel={t((m) => m.kitchen.availabilitySaving)}
        isSubmitting={isSaving}
        closeLabel={t((m) => m.common.cancel)}
      >
        {pendingIngredient?.isAvailable ? (
          <Checkbox
            size="lg"
            checked={untilManual}
            onChange={(event) => setUntilManual(event.target.checked)}
            label={t((m) => m.kitchen.availabilityIngredientUntilManual)}
            description={t((m) => m.kitchen.availabilityUntilManualHint)}
            data-testid="kitchen-ingredient-until-manual-check"
          />
        ) : null}
      </ConfirmModal>
    </Screen>
  );
}
